import { useEffect } from 'react';

const cache = {};

/**
 * Loads image by URL into canvas and calculates its most used color
 * Returns RGB object
 */
export const useImgDominantColor = (
    imgUrl,
    setImgDominantColor,
    dependencies = [],
) => {
    useEffect(() => {
        // get data from cache if color was calculated already
        if (imgUrl in cache) {
            setImgDominantColor(cache[imgUrl]);
            return;
        }

        const defaultRGB = {
            r: 0,
            g: 0,
            b: 0
        };

        const img = new Image();
        img.crossOrigin = '';
        img.onload = () => {
            const blockSize = 5;
            const canvas = document.createElement('canvas');
            const context = canvas.getContext && canvas.getContext('2d');
            let data;
            let width;
            let height;
            let i = -4;
            let pixelsProcessed = 0;
            const rgb = { r: 0, g: 0, b: 0 };

            if (!context) {
                cache[imgUrl] = defaultRGB;
                return defaultRGB;
            }

            height = canvas.height = img.naturalHeight || img.offsetHeight || img.height;
            width = canvas.width = img.naturalWidth || img.offsetWidth || img.width;

            context.drawImage(img, 0, 0);

            try {
                data = context.getImageData(0, 0, width, height);
            } catch {
                cache[imgUrl] = defaultRGB;
                return defaultRGB;
            }

            const imagePixelsTotal = data.data.length;

            // go through all pixels with step
            while ((i += blockSize * 4) < imagePixelsTotal) {
                pixelsProcessed += 1;
                // get RBB data of pixel
                rgb.r += data.data[i];
                rgb.g += data.data[i + 1];
                rgb.b += data.data[i + 2];
            }

            // calculate average RGB values of whole image
            rgb.r = Math.floor(rgb.r / pixelsProcessed);
            rgb.g = Math.floor(rgb.g / pixelsProcessed);
            rgb.b = Math.floor(rgb.b / pixelsProcessed);

            // save to cache
            cache[imgUrl] = rgb;

            setImgDominantColor(rgb);
        };

        img.onerror = () => {
            // save to cache default object
            // this will prevent from re-downloading image over and over
            cache[imgUrl] = defaultRGB;
        };

        // start img loading, should be before onload handler
        img.src = imgUrl;
    }, dependencies);
};
